import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, 
  Search, 
  Menu, 
  X, 
  User, 
  Settings, 
  HelpCircle,
  LogOut,
  UserCircle,
  Shield,
  CreditCard,
  ChevronDown,
  Clock,
  FileText,
  CheckCircle,
  AlertCircle,
  Info,
  Trash2,
  MoreVertical,
  Mail
} from 'lucide-react';
// IMPORTAR authService
import { authService } from '../../services/authService'; // Ajusta la ruta según tu proyecto

const Header = ({ onToggleSidebar, sidebarOpen }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  
  const userMenuRef = useRef(null);
  const searchRef = useRef(null);

  // Función para cargar los datos del usuario de manera segura (descifrando)
  const loadUserData = () => {
    try {
      const user = authService.getCurrentUser(); // Esto descifra automáticamente
      setUserData(user);
    } catch (error) {
      console.error('Error al cargar datos del usuario:', error);
      setUserData(null);
    }
  };

  // Cargar datos del usuario al montar y cada vez que cambie la clave 'user_encrypted'
  useEffect(() => {
    loadUserData();

    // Escuchar cambios en localStorage (cuando otro tab modifique el usuario)
    const handleStorageChange = (e) => {
      // La clave ahora es 'user_encrypted' (definida en authService)
      if (e.key === 'user_encrypted') {
        loadUserData();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Intervalo para verificar actualizaciones (cada 5 segundos) - opcional
    const intervalId = setInterval(loadUserData, 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  // Cerrar menús al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        if (!searchQuery) setSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchQuery]);

  // Opciones del menú de usuario (solo cerrar sesión)
  const userMenuOptions = [
    {
      label: 'Cerrar Sesión',
      icon: LogOut,
      action: () => handleLogout(),
      color: 'text-red-500'
    }
  ];

  // Función para obtener iniciales del nombre
  const getInitials = (nombre, apellido) => {
    if (!nombre) return 'U';
    if (apellido) {
      return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase();
    }
    return nombre.charAt(0).toUpperCase();
  };

  // Función para cerrar sesión (usando authService.logout o manual)
  const handleLogout = () => {
    // Usamos authService.logout() que limpia ambas claves
    authService.logout();
    // authService.logout ya redirige a '/login'
  };

  const handleSearch = (e) => {
    e.preventDefault();
    console.log('Buscar:', searchQuery);
  };

  return (
    <header className="bg-white border-b border-slate-200 shadow-sm relative z-40">
      <div className="flex items-center justify-between px-4 md:px-6 py-3">
        {/* Left Section */}
        <div className="flex items-center space-x-4 flex-1">
          <motion.button
            onClick={onToggleSidebar}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors lg:hidden"
          >
            <motion.div animate={{ rotate: sidebarOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
              {sidebarOpen ? <X className="w-5 h-5 text-slate-700" /> : <Menu className="w-5 h-5 text-slate-700" />}
            </motion.div>
          </motion.button>

          <div className="lg:hidden">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">V7</span>
              </div>
              <span className="text-sm font-semibold text-slate-900">Digital</span>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-2 md:space-x-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSearchOpen(true)}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors md:hidden"
          >
            <Search className="w-5 h-5 text-slate-600" />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors hidden md:flex"
          >
            <HelpCircle className="w-5 h-5 text-slate-600" />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors hidden md:flex"
          >
            <Settings className="w-5 h-5 text-slate-600" />
          </motion.button>

          {/* User Menu */}
          <div ref={userMenuRef} className="relative">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-3 pl-3 md:pl-4 border-l border-slate-200 hover:bg-slate-50 rounded-lg transition-colors py-1 pr-2"
            >
              <div className="relative">
                {userData?.logo_empresa ? (
                  <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-white shadow-lg">
                    <img 
                      src={userData.logo_empresa} 
                      alt={`${userData.nombre || ''}`} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${getInitials(userData.nombre)}&background=072146&color=fff&size=64`;
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-semibold text-sm">
                      {getInitials(userData?.nombre)}
                    </span>
                  </div>
                )}
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white shadow-sm" />
              </div>
              
              <div className="hidden md:block text-left">
                <p className="text-sm font-semibold text-slate-900 truncate max-w-[120px]">
                  {userData ? userData.nombre : 'Cargando...'}
                </p>
                <div className="flex items-center space-x-1">
                  <Mail className="w-3 h-3 text-slate-400" />
                  <p className="text-xs text-slate-500 truncate max-w-[140px]" title={userData?.email || ''}>
                    {userData?.email || 'usuario@Visioner7.com'}
                  </p>
                </div>
              </div>

              <motion.div animate={{ rotate: userMenuOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </motion.div>
            </motion.button>

            {/* User Dropdown Menu */}
            <AnimatePresence>
              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
                >
                  <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
                    <div className="flex items-center space-x-3">
                      {userData?.logo_empresa ? (
                        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-lg">
                          <img 
                            src={userData.logo_empresa} 
                            alt={`${userData.nombre || ''}`} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://ui-avatars.com/api/?name=${getInitials(userData.nombre)}&background=072146&color=fff&size=128`;
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-lg">
                            {getInitials(userData?.nombre)}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {userData ? userData.nombre : 'Usuario'}
                        </p>
                        <div className="flex items-center space-x-1">
                          <Mail className="w-3 h-3 text-slate-400" />
                          <p className="text-xs text-slate-500 truncate" title={userData?.email || ''}>
                            {userData?.email || 'usuario@Visioner7.com'}
                          </p>
                        </div>
                        {(userData?.posicion || userData?.nombre_empresa) && (
                          <div className="mt-1 space-y-1">
                            {userData?.posicion && (
                              <p className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded-full inline-block">
                                {userData.posicion}
                              </p>
                            )}
                            {userData?.nombre_empresa && (
                              <p className="text-xs text-slate-600 truncate">
                                {userData.nombre_empresa}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="py-2">
                    <div className="my-2 border-t border-slate-200" />
                    {userMenuOptions.map((option) => (
                      <motion.button
                        key={option.label}
                        onClick={option.action}
                        whileHover={{ x: 4 }}
                        className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
                      >
                        <option.icon className={`w-4 h-4 ${option.color}`} />
                        <span className={`text-sm font-medium ${option.color}`}>
                          {option.label}
                        </span>
                      </motion.button>
                    ))}
                  </div>

                  <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>V7 Digital</span>
                      <span>v2.0</span>
                    </div>
                    {userData?.nombre_empresa && (
                      <p className="text-xs text-slate-400 mt-1 text-center truncate">
                        {userData.nombre_empresa}
                      </p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Search Modal para mobile */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-0 left-0 right-0 bg-white border-b border-slate-200 px-4 py-3 shadow-lg md:hidden"
          >
            <div className="flex items-center space-x-2" ref={searchRef}>
              <Search className="w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar en el sistema..."
                className="flex-1 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none"
                autoFocus
              />
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSearchOpen(false);
                }}
                className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(100, 116, 139, 0.3) transparent;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(100, 116, 139, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(100, 116, 139, 0.5);
        }
      `}</style>
    </header>
  );
};

export default Header;